import { createShapeNormalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';

const variant2Shape = {
	type: 'object',
	shape: {
		corgi: {
			type: 'object',
			shape: {
				membership: {
					type: 'object',
					shape: {
						manage: {
							type: 'object',
							shape: {
								cancel: {
									type: 'object',
									shape: {
										cancel_modal: {
											type: 'object',
											shape: {
												easy_cancel_copy: {
													type: 'object',
													shape: {
														headline: { type: 'string' },
														description: { type: 'string' },
														learn_more_link_text: { type: 'string' },
													},
												},
											},
										},
									},
								},
							},
						},
					},
				},
			},
		},
	},
} as const;

const variant3Shape = {
	type: 'object',
	shape: {
		corgi: {
			type: 'object',
			shape: {
				membership: {
					type: 'object',
					shape: {
						manage: {
							type: 'object',
							shape: {
								cancel: {
									type: 'object',
									shape: {
										cancel_modal: {
											type: 'object',
											shape: {
												easy_cancel_copy: {
													type: 'object',
													shape: {
														headline: { type: 'string' },
														description: { type: 'string' },
													},
												},
											},
										},
									},
								},
							},
						},
					},
				},
			},
		},
	},
} as const;

/**
 * Nests `easyCancelCopy` under the i18n key path used by the manage-membership cancel modal.
 *
 * Generic over the leaf so each variant's default keeps its precise key set: variant_2 carries a
 * required `learn_more_link_text`, variant_3 has none — matching each variant's schema shape above.
 */
function copyValue<C extends { headline: string; description: string; learn_more_link_text?: string }>(
	easyCancelCopy: C,
) {
	return {
		corgi: {
			membership: {
				manage: {
					cancel: {
						cancel_modal: {
							easy_cancel_copy: easyCancelCopy,
						},
					},
				},
			},
		},
	};
}

const EN_US_DEFAULT = {
	variant_2: copyValue({
		headline: "Before you go: you've helped power real wins this year",
		description:
			"Members like you are the reason petitions get real responses from decision-makers. If it's the cost, not the cause, lowering to %{amount}/mo keeps you in it.",
		learn_more_link_text: 'Learn more about your impact',
	}),
	variant_3: copyValue({
		headline: 'Before you go: your membership is what keeps this platform independent',
		description:
			"Change.org doesn't take money from corporations or governments — we run 100% on small donations from people like you. Lowering to %{amount}/mo keeps your support in the fight.",
	}),
};

export const FCM_MEMBERSHIP_EASY_CANCEL_EXPERIMENT_COPY = defineFcm('membership_easy_cancel_experiment_copy')
	.asJson(
		createShapeNormalizer({
			type: 'object',
			shape: {
				variant_2: variant2Shape,
				variant_3: variant3Shape,
			},
		}),
	)
	.withDefault(EN_US_DEFAULT)
	.toConfig({
		owner: 'squad-koi',
		deprecated:
			'Copy productized into corgi.en-US.yml (CHANGE-99090) — front-end usage removed; safe to delete after 2026-09-29',
		scope: 'corgi-front-end',
		context: 'One Click Subscription Cancel Page',
		description: `Locale-aware, per-variant copy for the membership_easy_cancel Amplitude experiment (CHANGE-95282), served through
			I18nOverrides per docs/packages/experiments/i18n-copy.md (FC: multiple variants). Bucketing/rollout is
			controlled by the membership_easy_cancel Amplitude flag; control has no entry (it renders the existing production
			copy). Set per-locale values (AI translations) in the Admin UI; the %{amount} placeholder is replaced at render
			time with the localized lower-amount value (e.g. "$5") computed the same way as the lower-amount button, and the
			per-month suffix (e.g. "/mo") is baked into each locale's copy and localized — translations must preserve the
			%{amount} placeholder.`,
		supportedGroups: { global: true, locales: true },
		template: EN_US_DEFAULT,
	});
